import React, { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import {
  Box, Button, useMediaQuery,
} from '@mui/material';
import { useDestrierTheme } from '../ThemeContext';
import { fontSize as fsLanding, WhiteCTAButton } from './landing/shared';
import HeroMockup from './landing/HeroMockup';
import NeatBackground from './landing/NeatBackground';
import { pickHeroPlaceholder, HERO_SEED } from './landing/hero-gradient';
import { WORD_IMAGE } from './landing/word-gradient';
// PainSection is eager — it's the section immediately after TrustBand, so the
// page must have scroll height there at first paint (otherwise users hit the
// bottom of the hero+band and can't scroll until lazy chunks resolve).
import PainSection from './landing/PainSection';

// Below-fold sections are code-split. webpackPreload makes the browser fetch
// these chunks in parallel with the main bundle (more aggressive than Prefetch),
// so by the time React renders the page they're already in the browser cache.
const StartYourAscension = lazy(() => import(/* webpackPrefetch: true */ './landing/StartYourAscension'));
const ChessArchetypeDarkSection = lazy(() => import(/* webpackPrefetch: true */ './landing/ChessArchetypeDarkSection'));
const PlayerTestimonials = lazy(() => import(/* webpackPrefetch: true */ './landing/PlayerTestimonials'));
const FAQSection = lazy(() => import(/* webpackPrefetch: true */ './landing/FAQSection'));
const FinalCTA = lazy(() => import(/* webpackPrefetch: true */ './landing/FinalCTA'));
const Footer = lazy(() => import(/* webpackPrefetch: true */ './landing/Footer'));
const PremiumMembership = lazy(() => import(/* webpackPrefetch: true */ './landing/PremiumMembership'));
const AnalysisShowcase = lazy(() => import(/* webpackPrefetch: true */ './landing/DashboardShowcase').then((m) => ({ default: m.AnalysisShowcase })));
const TrainingShowcase = lazy(() => import(/* webpackPrefetch: true */ './landing/DashboardShowcase').then((m) => ({ default: m.TrainingShowcase })));
const CoachComparison = lazy(() => import(/* webpackPrefetch: true */ './landing/CoachComparison'));


// ---- Palettes ----
const destrierPalette = {
  bg: '#222226',
  bgCard: '#252528',
  bgCardHover: '#2d2d32',
  accent: '#8A74E4',
  accentGlow: '#8A74E440',
  blue: '#00A5C4',
  ctaBlue: '#3DB5D4',
  blueGlow: '#00A5C430',
  gold: '#DE7345',
  textPrimary: '#ECE2DB',
  textSecondary: '#8a8a9a',
  textMuted: '#5a5a6a',
  border: '#ffffff0a',
  borderHover: '#ffffff15',
};

const useColors = () => {
  const { c } = useDestrierTheme();
  if (c.mode === 'dark') {
    return { ...destrierPalette, isDark: true, cream: c.cream, textDark: c.textDark };
  }
  return {
    ...destrierPalette,
    isDark: false,
    cream: c.cream,
    textDark: c.textDark,
    bg: '#ECE2DB',
    bgCard: '#E4D8CF',
    bgCardHover: '#ddd0c5',
    textPrimary: '#222226',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    border: '#00000008',
    borderHover: '#00000012',
  };
};

// ---- Fade-in on scroll ----
// ---- Section Chevron (clickable scroll-to-next) ----
// ============================================================
//  NAV
function Nav({ scrollTo, isGenerating }) {
  const c = useColors();
  const [scrolled, setScrolled] = useState(false);
  const [ctaBlue, setCtaBlue] = useState(false); // eslint-disable-line no-unused-vars
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => setCtaBlue(true), 9400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const sectionToLink = {
      'archetypes': 'profiles',
      'how-it-works': 'how-it-works',
      'features': 'features',
      'pricing': 'pricing',
      'faq': 'faq',
    };
    const sectionIds = Object.keys(sectionToLink);
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
      const scrollPos = window.scrollY + window.innerHeight * 0.4;
      let current = null;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollPos) current = sectionToLink[id];
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: 'Archetypes', id: 'profiles' },
    { label: 'How it works', id: 'how-it-works' },
    { label: 'Features', id: 'features' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <Box
      id="site-nav"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        pointerEvents: isGenerating ? 'none' : 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, md: 3 },
        py: { xs: 1, md: 1.5 },
        // Background only appears once the user scrolls past the top of the hero.
        background: scrolled ? (c.isDark ? 'rgba(28,28,34,0.55)' : `${c.bg}80`) : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.isDark ? 'rgba(255,255,255,0.06)' : c.border}` : '1px solid transparent',
        transition: 'background 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease',
        '&::after': scrolled ? {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("${GRAIN_DATA_URL}")`,
          backgroundRepeat: 'repeat',
          pointerEvents: 'none',
          opacity: 0.6,
        } : undefined,
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        role="button"
        aria-label="Scroll to top"
      >
        <img src={c.isDark ? '/logo-light.svg' : '/logo.svg'} alt="Destrier" style={{ width: 32, height: 32, marginBottom: 2 }} />
        <span style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: 'clamp(1.15rem, min(1.5vw, 2.3vh), 1.65rem)', color: c.textPrimary, letterSpacing: '-0.02em' }}>
          Destrier<span style={{ color: '#8A74E4' }}>.ai</span>
        </span>
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
        {links.map((link) => {
          const isActive = link.id === activeId;
          return (
            <Button
              key={link.id}
              variant="text"
              onClick={() => scrollTo(link.id)}
              sx={{
                color: isActive ? c.textPrimary : c.textSecondary,
                fontWeight: isActive ? 500 : 400,
                textTransform: 'none',
                fontSize: 'clamp(0.85rem, min(0.85rem + 0.3vw, 1.8vh), 1.1rem)',
                px: 2,
                minWidth: 'auto',
                borderRadius: '100px',
                transition: 'color 0.3s, font-weight 0.2s',
                '&:hover': { color: c.textPrimary, backgroundColor: `${c.accent}08` },
              }}
            >
              {link.label}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="text"
          onClick={() => window.location.href = '/login'}
          sx={{
            color: c.textSecondary,
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '100px',
            px: { xs: 1.5, sm: 2.5 },
            fontSize: 'clamp(0.85rem, min(0.85rem + 0.3vw, 1.8vh), 1.1rem)',
            '&:hover': { color: c.textPrimary, backgroundColor: `${c.accent}10` },
          }}
        >
          Login
        </Button>
        <WhiteCTAButton
          size="small"
          arrow={false}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            px: { xs: 1.5, sm: 3 },
            fontWeight: 500,
            fontSize: 'clamp(0.85rem, min(0.85rem + 0.3vw, 1.8vh), 1.1rem)',
          }}
        >
          Join the Waitlist
        </WhiteCTAButton>
      </Box>
    </Box>
  );
}

function RevealLine({ children, style }) {
  return (
    <span style={{ display: 'block', ...style }}>
      {children}
    </span>
  );
}

function HeroSection() {
  const c = useColors();
  // Shorter viewports (MacBook-class laptops) get a smaller CTA so it doesn't
  // crowd the mockup. Width gate avoids triggering on mobile-portrait.
  const isShortHeight = useMediaQuery('(max-height: 1200px) and (min-width: 900px)');

  // All hero elements fade in together — no staggered delays.
  const d = {
    tagline: 0,
    title1: 0,
    title2: 0,
    subtitle1: 0,
    subtitle2: 0,
    cta: 0,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        // On phones, fit the hero to its content. With 100dvh the inner
        // text block stretches to fill, leaving variable empty space
        // between the CTA and the mockup depending on phone height.
        minHeight: { xs: 'auto', md: '102vh' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Hero content — gradient + fade now live at the #hero level (in LandingPageB)
          so they extend behind the mockup card. This Box just holds the text layout. */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          flex: '1 1 auto',
          px: { xs: 3, md: 6 },
          pt: { xs: 8, md: '7.5vh' },
          // Mobile: trim the bottom padding so the gap below the CTA matches
          // the subtitle→CTA gap above (~32px) instead of dwarfing it.
          pb: { xs: 4, md: 12 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(2rem, 4vw, 3rem)',
          // Sit above the mockup wrapper (zIndex 2) so the CTA at the
          // bottom of this text block remains hoverable — the mockup's
          // negative mt pulls its bounding rect up over the CTA region.
          zIndex: 3,
          // The box itself stretches down over the top of the mockup on tall
          // screens, so it must not swallow clicks there (it blocked the radar's
          // "Rook Endgames" label). Only the actual content takes clicks.
          pointerEvents: 'none',
        }}
      >
        {/* Title + subtitle, centered vertically in the remaining space.
            No z-index here and filter only applied when blurred (not blur(0px))
            so the h1's mix-blend-mode can reach the gradient below. */}
        <Box sx={{ position: 'relative', flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 'clamp(1.25rem, min(2.4vw, 2.2vh), 2rem)', textAlign: 'center', maxWidth: 1100, mx: 'auto', mt: 'clamp(3rem, 7vh, 7.5rem)', '& > *': { pointerEvents: 'auto' } }}>
          {/* Tagline pill — same format as the "Player Stories" pill in the
              testimonials section: accent-tinted background, accent border,
              uppercase accent text. */}
          <Box
            sx={{
              display: 'inline-block',
              px: 2,
              py: 0.5,
              borderRadius: '100px',
              background: `${c.accent}15`,
              border: `1px solid ${c.accent}25`,
              // Static label: no hover or click reaction.
              cursor: 'default',
            }}
          >
            <Box
              component="span"
              sx={{
                color: c.accent,
                // vh-bounded so the pill shrinks on shorter viewports
                // (MBP-class) without changing on widescreens.
                // 3vw cap + lower min so the long "AI-POWERED CHESS ANALYTICS
                // PLATFORM" string fits on one row on phone-width viewports.
                fontSize: 'clamp(0.7rem, min(0.9rem + 0.45vw, 2vh, 3vw), 1.45rem)',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              AI-powered chess analytics platform
            </Box>
          </Box>

          <h1
            style={{
              color: '#ECE2DB',
              // min(8vw, 7.5vh) keeps the title from being huge on short
              // viewports (the vw cap dominates on widescreens; the vh cap
              // dominates on MacBook-class laptops).
              fontSize: 'clamp(2.4rem, min(8vw, 8.5vh), 6.2rem)',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            <RevealLine delay={d.title1}>Improve your chess elo.</RevealLine>
            <RevealLine delay={d.title2}>
              {/* Still frame of the FinalCTA-style gradient + grain, clipped to
                  the letters — see word-gradient.js. The image spans the word's
                  full width (so the fade does at any size), centered vertically. */}
              <span
                style={{
                  display: 'inline-block',
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                  backgroundImage: `url("${HERO_GRAIN_DATA_URL}"), url("${WORD_IMAGE.src}")`,
                  backgroundSize: 'auto, 100% auto',
                  backgroundPosition: '0 0, center',
                  backgroundRepeat: 'repeat, no-repeat',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                }}
              >
                Drastically.
              </span>
            </RevealLine>
          </h1>

          <div
            style={{
              color: c.textPrimary,
              // vh-bounded so subtitle scales down on shorter viewports too.
              fontSize: 'clamp(0.95rem, min(2.1vw, 2.4vh), 1.5rem)',
              lineHeight: 1.5,
              margin: 'clamp(1rem, 2vw, 1.75rem) 0 0',
              maxWidth: 1000,
            }}
          >
            <RevealLine delay={d.subtitle1}>
              <Box component="span" sx={{ whiteSpace: { xs: 'normal', md: 'nowrap' } }}>Destrier analyzes your <strong>chess.com</strong> games, finds your weaknesses, and gives you</Box>
            </RevealLine>
            <RevealLine delay={d.subtitle2}>
              <Box component="span" sx={{ whiteSpace: { xs: 'normal', md: 'nowrap' } }}>a <strong>fully personalized</strong> training plan within <strong>30 seconds</strong>.</Box>
            </RevealLine>
          </div>

          <div style={{ marginTop: 'clamp(0.5rem, min(3vw, 2.8vh), 2.5rem)' }}>
            <WhiteCTAButton
              size={isShortHeight ? 'medium' : 'large'}
              onClick={() => document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Start your ascension
            </WhiteCTAButton>
          </div>
        </Box>
      </Box>
    </Box>
  );
}
// ============================================================
const TRUST_LOGOS = [
  {
    name: 'Chess.com',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5.5" r="3.5" />
        <path d="M9.5 8.5C8 10 7.5 12 8 13.5h8c.5-1.5 0-3.5-1.5-5h-5z" />
        <path d="M7 15.5c-.5 0-1 .5-1 1v1h12v-1c0-.5-.5-1-1-1H7z" />
        <rect x="6" y="19" width="12" height="2.5" rx="1" />
      </svg>
    ),
    label: 'Chess.com',
  },
  {
    name: 'Stockfish',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.41 11.84c-.2-.4-.6-.7-1.1-.8l-3-1c-.5-.2-1.1 0-1.3.5l-.2.3c-.2-.1-.4-.1-.6-.1h-1.6l-.6-1.5c.7-.3 1.2-1 1.2-1.8 0-1.1-.9-2-2-2h-.4l-.5-1.3c-.2-.5-.8-.7-1.3-.5-.5.2-.7.8-.5 1.3l.3.6c-.6.3-1 .9-1 1.7 0 .2 0 .3.1.5l-2.9 1.4h-.5c-1.1 0-2 .7-2.4 1.6l-3.1.9c-.5.1-.9.5-1 1s.1 1 .4 1.3l3 2.3c.2.2.5.3.8.3.1 0 .2 0 .3-.1l1.2-.3c.4.8 1.2 1.3 2.1 1.3h.7l.2.5c.4 1 1.4 1.6 2.4 1.6.3 0 .6-.1.9-.2l2.9-1.1c.5-.2.8-.5 1-1l.6-1.8 2.5-.7c.5-.1.9-.5 1-1l.5-2c.1-.3 0-.6-.2-.9z" />
      </svg>
    ),
    label: 'Stockfish',
  },
  {
    name: 'AWS',
    icon: (
      <svg width="24" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6.76 10.74c0 .27.03.49.08.65.06.17.13.35.22.53.03.06.05.12.05.17 0 .07-.05.15-.14.22l-.47.31c-.07.05-.13.07-.19.07-.07 0-.15-.04-.22-.11a2.26 2.26 0 01-.27-.35 5.1 5.1 0 01-.23-.44c-.58.69-1.31 1.03-2.19 1.03-.63 0-1.13-.18-1.49-.53-.37-.36-.56-.83-.56-1.42 0-.63.22-1.14.67-1.52.45-.39 1.04-.58 1.8-.58.25 0 .51.02.78.06.27.04.55.1.85.18v-.54c0-.56-.12-.96-.35-1.18-.24-.23-.64-.34-1.22-.34-.26 0-.53.03-.81.1-.28.06-.55.15-.81.26-.12.05-.21.08-.27.1a.45.45 0 01-.12.02c-.1 0-.16-.08-.16-.23v-.37c0-.12.01-.21.05-.27.03-.06.1-.12.22-.18.26-.13.57-.25.94-.34A4.5 4.5 0 014.48 6c.74 0 1.28.17 1.63.5.34.34.52.85.52 1.53v2.02h.13zm-3.02 1.13c.24 0 .5-.04.76-.13.27-.09.51-.25.71-.47.12-.14.21-.3.27-.49.05-.19.08-.42.08-.69v-.33a6.14 6.14 0 00-.68-.14 5.57 5.57 0 00-.69-.05c-.49 0-.85.1-1.09.31-.24.2-.36.49-.36.87 0 .35.09.62.28.8.18.19.45.29.8.29l-.08.03zm5.97.87c-.13 0-.22-.02-.28-.07-.06-.04-.12-.14-.16-.28l-1.8-5.93c-.04-.15-.07-.25-.07-.3 0-.12.06-.18.18-.18h.57c.14 0 .23.02.28.07.06.04.11.14.16.28l1.29 5.07 1.2-5.07c.04-.15.09-.24.15-.28.06-.04.16-.07.29-.07h.47c.14 0 .23.02.29.07.06.04.11.14.15.28l1.21 5.12 1.33-5.12c.04-.15.1-.24.16-.28.06-.04.15-.07.28-.07h.54c.12 0 .18.06.18.18 0 .04-.01.07-.02.12-.01.04-.03.1-.06.19l-1.85 5.93c-.04.15-.1.24-.16.28-.06.04-.15.07-.28.07h-.5c-.14 0-.23-.02-.29-.07-.06-.05-.11-.14-.15-.29l-1.19-4.93-1.18 4.92c-.04.15-.09.24-.15.29-.06.05-.16.07-.29.07h-.5l.01.01zm9.54.23c-.39 0-.77-.05-1.14-.14-.37-.09-.66-.19-.85-.31-.12-.07-.2-.15-.23-.22a.56.56 0 01-.04-.2v-.39c0-.15.06-.23.17-.23.05 0 .1.01.14.03.05.02.12.05.2.1.28.14.58.25.9.34.33.09.64.13.96.13.51 0 .91-.09 1.18-.28.28-.19.42-.46.42-.81 0-.24-.08-.43-.23-.58-.15-.16-.44-.29-.86-.42l-1.23-.38c-.62-.19-1.08-.48-1.36-.86a2.02 2.02 0 01-.42-1.22c0-.35.08-.66.23-.93s.35-.5.6-.68c.25-.18.54-.32.86-.41.33-.09.67-.14 1.03-.14.18 0 .37.01.56.04.19.03.37.07.54.11.17.05.33.1.48.16.15.06.26.12.35.18.12.07.2.15.25.22.04.07.07.17.07.28v.36c0 .15-.06.23-.17.23-.06 0-.16-.03-.29-.1-.44-.2-.93-.3-1.48-.3-.46 0-.83.07-1.08.23-.25.16-.38.39-.38.72 0 .24.08.44.25.6.17.16.48.32.93.46l1.21.38c.61.19 1.06.46 1.32.81.27.35.4.75.4 1.2 0 .36-.07.69-.22.97-.15.29-.36.54-.62.74-.26.2-.58.36-.94.46-.38.12-.78.17-1.21.17l.01.01z" />
        <path d="M21.37 16.48c-2.6 1.92-6.37 2.94-9.61 2.94-4.55 0-8.64-1.68-11.74-4.48-.24-.22-.03-.52.27-.35 3.34 1.95 7.48 3.12 11.75 3.12 2.88 0 6.05-.6 8.96-1.83.44-.18.81.29.37.6z" />
        <path d="M22.43 15.28c-.33-.43-2.2-.2-3.04-.1-.26.03-.29-.19-.06-.36 1.49-1.05 3.94-.75 4.22-.39.28.35-.08 2.81-1.47 3.99-.21.18-.42.09-.32-.15.31-.79 1.01-2.56.67-2.99z" />
      </svg>
    ),
    label: 'AWS',
  },
  {
    name: 'Python',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.91 0C5.85 0 6.24 2.63 6.24 2.63l.01 2.73h5.77v.82H3.89S0 5.72 0 11.84s3.39 5.9 3.39 5.9h2.02v-2.84s-.11-3.39 3.34-3.39h5.74s3.23.05 3.23-3.12V3.2S18.38 0 11.91 0zm-3.2 1.85a1.04 1.04 0 110 2.09 1.04 1.04 0 010-2.09z" />
        <path d="M12.09 24c6.06 0 5.67-2.63 5.67-2.63l-.01-2.73h-5.77v-.82h8.13S24 18.28 24 12.16s-3.39-5.9-3.39-5.9h-2.02v2.84s.11 3.39-3.34 3.39H9.51s-3.23-.05-3.23 3.12v5.19S5.62 24 12.09 24zm3.2-1.85a1.04 1.04 0 110-2.09 1.04 1.04 0 010 2.09z" />
      </svg>
    ),
    label: 'Python',
  },
  {
    name: 'PyTorch',
    icon: (
      <svg width="20" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.005 0L4.952 7.053a9.865 9.865 0 000 14.022 9.866 9.866 0 0014.022 0c3.984-3.9 3.986-10.205.085-14.023l-1.744 1.743c2.904 2.905 2.904 7.634 0 10.538s-7.634 2.904-10.538 0-2.904-7.634 0-10.538l4.647-4.646.582-.665z" />
        <path d="M15.573 3.9a1.327 1.327 0 00-1.327 1.327 1.327 1.327 0 001.327 1.328A1.327 1.327 0 0016.9 5.226 1.327 1.327 0 0015.573 3.9z" />
      </svg>
    ),
    label: 'PyTorch',
  },
  {
    name: 'TensorFlow',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M1.292 5.856L11.54 0v24l-4.095-2.378V7.603l-6.168 3.564.015-5.31z" />
        <path d="M21.43 11.167l-.014-5.31L12.46 0v24l4.095-2.378V14.87l3.092 1.788-.018-4.618-3.074-1.756V7.603l6.168 3.564z" />
      </svg>
    ),
    label: 'TensorFlow',
  },
];

function TrustBand({ knockout }) {
  const c = useColors();

  const trackGap = { xs: '32px', sm: '52px', md: '72px' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, userSelect: 'none' }}>
      <Box
        id="trust-logos-row"
        sx={{
          // Knockout mode: the row only provides the layout; what you see is
          // TrustLogoKnockout drawing the gradient through the logo shapes.
          visibility: knockout ? 'hidden' : 'visible',
          width: '100%',
          maxWidth: 1500,
          px: { xs: 2, md: 4 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: trackGap,
          color: c.textPrimary,
          opacity: 0.38,
        }}
      >
        {TRUST_LOGOS.map((logo) => (
          <Box key={logo.name} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.4, sm: 2.4 }, flexShrink: 0, '& svg': { transform: { xs: 'scale(1.7)', sm: 'scale(2.6)' } } }}>
            {logo.icon}
            <Box component="span" sx={{ fontWeight: 600, letterSpacing: '-0.01em', whiteSpace: 'nowrap', fontSize: { xs: '1.6rem', sm: '2.1rem' } }}>{logo.label}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}


// Dark-mode trust band logos as see-through cut-outs: a layer clipped to the
// logo + label shapes whose backdrop-filter shows the hero gradient behind the
// band in its natural colors. It must sit OUTSIDE #trust-band-glass (an
// element with backdrop-filter is a backdrop root, so anything inside it would
// only see the glass tint). The shapes are measured off the hidden logo row so
// layout (wrapping, breakpoints, fonts) stays owned by TrustBand.
function TrustLogoKnockout() {
  const ref = useRef(null);
  const [shapes, setShapes] = useState(null);

  useLayoutEffect(() => {
    const row = document.getElementById('trust-logos-row');
    if (!row || !ref.current) return undefined;
    const measure = () => {
      const base = ref.current.getBoundingClientRect();
      setShapes([...row.children].map((item) => {
        const icon = item.querySelector('svg').getBoundingClientRect();
        const label = item.querySelector('span');
        const lr = label.getBoundingClientRect();
        const cs = getComputedStyle(label);
        const k = Math.min(icon.width, icon.height) / 24;
        return {
          iconTransform: `translate(${icon.left - base.left + (icon.width - 24 * k) / 2} ${icon.top - base.top + (icon.height - 24 * k) / 2}) scale(${k})`,
          textX: lr.left - base.left,
          textY: lr.top - base.top + lr.height / 2,
          font: { fontFamily: cs.fontFamily, fontSize: cs.fontSize, fontWeight: cs.fontWeight, letterSpacing: cs.letterSpacing },
        };
      }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    ro.observe(ref.current);
    document.fonts?.ready.then(measure);
    return () => ro.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        pointerEvents: 'none',
        visibility: shapes ? 'visible' : 'hidden',
        clipPath: 'url(#trustLogoClip)',
        // Natural colors: the backdrop here already includes the glass's dark
        // 42% tint, so a brightness lift just undoes that tint — no saturation
        // boost, the letters show the gradient as it is. A soft cream base
        // keeps them readable where the gradient behind is dark; where there's
        // color, the color still shows through.
        backgroundColor: 'rgba(236, 226, 219, 0.2)',
        backdropFilter: 'brightness(1.7)',
        WebkitBackdropFilter: 'brightness(1.7)',
      }}
    >
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id="trustLogoClip" clipPathUnits="userSpaceOnUse">
            {shapes && TRUST_LOGOS.map((logo, i) => (
              <React.Fragment key={logo.name}>
                {/* <clipPath> ignores <g>, so each icon shape carries the transform. */}
                {React.Children.map(logo.icon.props.children, (shape) =>
                  React.cloneElement(shape, { transform: shapes[i].iconTransform }))}
                <text x={shapes[i].textX} y={shapes[i].textY} dominantBaseline="central" style={shapes[i].font}>
                  {logo.label}
                </text>
              </React.Fragment>
            ))}
          </clipPath>
        </defs>
      </svg>
    </Box>
  );
}

// Sections extracted to ./landing/ directory and lazy-loaded above

// ============================================================
//  MAIN — LandingPageB
// ============================================================
// Section wrapper: background in ::before (z-index 0), content at z-index 2
// This lets the cursor backing circle (z-index 1) float between bg and content
function Section({ id, bg, children, style }) {
  return (
    <Box
      id={id}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...style,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: bg,
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("${GRAIN_DATA_URL}")`,
          backgroundRepeat: 'repeat',
          zIndex: 1,
          pointerEvents: 'none',
        },
        '& > *': {
          position: 'relative',
          zIndex: 2,
        },
      }}
    >
      {children}
    </Box>
  );
}

// How far the pain section overlaps the bottom of the dark-mode reveal strip:
// the strip's visible height below the band (its height + negative marginTop,
// same clamps as the strip) minus the 90px of fade left showing above it.
const PAIN_OVERLAP = 'max(0px, calc(clamp(340px, 45vh, 570px) + clamp(-280px, -33vh, -220px) - 90px))';

// Generate a tiling noise texture as data URL (created once, reused everywhere)
const GRAIN_DATA_URL = (() => {
  if (typeof document === 'undefined') return '';
  const size = 200;
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  const imageData = ctx.createImageData(size, size);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.random() * 80;
    d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 0;
  }
  ctx.putImageData(imageData, 0, 0);
  return c.toDataURL('image/png');
})();

// Visible monochrome film grain for the trust band. Unlike GRAIN_DATA_URL above
// (whose pixels are fully transparent — alpha 0 — so it renders invisibly), this
// one writes a real alpha per pixel so the speckle actually shows over the glass.
// Full-range luminance noise (light + dark specks) reads well over the dark band.
const BAND_GRAIN_DATA_URL = (() => {
  if (typeof document === 'undefined') return '';
  const size = 200;
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  const imageData = ctx.createImageData(size, size);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.random() * 255;
    d[i] = v; d[i + 1] = v; d[i + 2] = v;
    d[i + 3] = Math.random() * 13; // real alpha → actually visible
  }
  ctx.putImageData(imageData, 0, 0);
  return c.toDataURL('image/png');
})();

// Grain for the shared hero gradient. The shader's own grain is off there (see
// the hero NeatBackground config): inside the canvas it sits under the gray
// fade and gets dimmed along with the color. Painted as its own layers instead,
// with a fade independent of the color fade (see HERO_GRAIN_FADE).
const HERO_GRAIN_DATA_URL = (() => {
  if (typeof document === 'undefined') return '';
  const size = 256;
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  const imageData = ctx.createImageData(size, size);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.random() < 0.5 ? 0 : 255;
    d[i] = v; d[i + 1] = v; d[i + 2] = v;
    // Grain strength. 7 measures the same pixel-noise level as the FinalCTA
    // shader grain (grainIntensity 0.1).
    d[i + 3] = Math.random() * 7;
  }
  ctx.putImageData(imageData, 0, 0);
  return c.toDataURL('image/png');
})();

// Grain fade over the hero (top → bottom, % of the hero's height). Starts higher
// than the color fade (solid gray to 18%, transparent by 82%) and ramps in over a
// much shorter distance, so the grain reaches further up with a harsher edge.
const HERO_GRAIN_FADE = 'linear-gradient(to bottom, transparent 10%, black 24%)';

export default function LandingPageB() {
  const { c } = useDestrierTheme();
  // Chosen once, for the viewport width at load (must stay the same image the
  // live hero gradient is rendered to match).
  const [heroPlaceholder] = useState(pickHeroPlaceholder);
  const isGenerating = false;
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{
      // clip, not hidden: hidden makes this a scroll container and breaks the
      // sticky stages in the analysis / training scroll stories.
      overflowX: 'clip',
      position: 'relative',
      letterSpacing: '0.05em',
      // Unified title weight across the landing page — every h1/h2 title uses the
      // same in-between weight (700), between the old hero/section 600 and the
      // heavier 800 used on a few headings. !important so it overrides the inline
      // fontWeight set on individual <h1>/<h2> elements.
      '& h1, & h2': { letterSpacing: '-0.03em', fontWeight: '650 !important' },
      // Minimum body-text size across the landing page (applies to everything
      // except the final CTA section and the footer, which have their own scale).
      '& :is(p, li, .MuiTypography-body1, .MuiTypography-body2, .MuiTypography-caption):not(:is(#cta-section, #cta-section *, #footer-section, #footer-section *))': {
        fontSize: fsLanding.link,
      },
      // Short-viewport scaling: section h2/h3 titles use vw-only clamps that
      // peg to their max on MacBook-class displays. Cap them with vh too so
      // the rest of the page mirrors the hero's proportional shrink. The
      // hero (#hero, including the mockup) is excluded — it has its own
      // already-tuned sizing.
      // Mobile width: section h2's use whiteSpace: nowrap on inner spans to
      // avoid awkward mid-phrase wraps on desktop, but on phones that pushes
      // text wider than the viewport and shifts the layout off-center. Allow
      // wrap here so phone layouts stay centered.
      '@media (max-width: 700px)': {
        '& h2 span': { whiteSpace: 'normal !important' },
      },
      '@media (max-height: 1200px) and (min-width: 900px)': {
        '& h2:not(#hero h2)': {
          fontSize: 'clamp(1.7rem, min(5.5vw, 5.5vh), 3.4rem) !important',
          lineHeight: '1.15 !important',
        },
        '& h3:not(#hero h3)': {
          fontSize: 'clamp(1.2rem, min(3vw, 3.3vh), 2rem) !important',
          lineHeight: '1.2 !important',
        },
        // Section subtitles (the descriptive line below each h2) are usually
        // <p> tags pegged to clamp(1.15rem, 2.1vw, 1.5rem), which lands at
        // 1.5rem (24px) on MacBook-class displays — much bigger than the
        // hero subtitle's vh-bounded size. Cap them with the same formula
        // the hero uses so they match. cta-section and footer-section keep
        // their own scale (excluded just like the global body-text rule).
        '& :is(p, li, .MuiTypography-body1, .MuiTypography-body2):not(:is(#hero *, #cta-section, #cta-section *, #footer-section, #footer-section *, #archetypes-intro *))': {
          fontSize: 'clamp(0.95rem, min(2.1vw, 2.4vh), 1.5rem) !important',
        },
        // Section pills (the small accent-colored uppercase tag above each
        // section title — "Chess Archetypes", "Before & After", etc.) are
        // inline-styled spans pegged to fontSize.link, which lands at ~21px
        // on MBP. Cap them with the same vh-bounded formula the hero pill
        // uses so all pills are the same size.
        '& span[style*="text-transform: uppercase"]': {
          fontSize: 'clamp(0.9rem, min(0.9rem + 0.45vw, 2vh), 1.45rem) !important',
        },
      },
    }}>
      {/* CustomCursor now lives in App.js */}
        <Nav scrollTo={scrollTo} isGenerating={isGenerating} />
        {/* ONE continuous gradient (TEST v4) spans the hero bottom + trust band +
            reveal. The hero base (gray, fading to transparent at its bottom), the
            opaque gray TrustBand, and the reveal strip are all rendered IN FRONT of
            this single <NeatBackground> and "cut" it — so the glow at the hero
            bottom and the glow below the band are literally the SAME moving
            gradient, just interrupted by the band. Dark mode only.
            (The LIKED circular-glow state is saved in memory:
            project_hero_gradient_preferred.) */}
        <Box sx={{ position: 'relative', '& a[data-n]': { display: 'none !important' } }}>
          {c.mode === 'dark' && (
            <NeatBackground
              // Pre-rendered first frame, shown from the first paint; the live
              // gradient takes over from it once running (see NeatBackground).
              placeholder={heroPlaceholder}
              // Overscan the gradient canvas so its edges (where the WebGL plane
              // doesn't fully cover wide aspect ratios) sit off-screen instead of
              // showing dark margins on the sides. Root has overflowX: hidden.
              style={{ left: '-12%', top: '-12%', width: '124%', height: '124%' }}
              config={{
                // Grain is drawn as separate layers (HERO_GRAIN_DATA_URL) with its
                // own fade, not by the shader — see HERO_GRAIN_FADE.
                grainIntensity: 0,
                // Fixed start frame so the pre-rendered placeholder
                // (landing/hero-gradient) matches the live gradient. Changing the
                // seed or anything else here means regenerating those images.
                seed: HERO_SEED,
                // GPU-independent noise (our @firecms/neat patch): without it each
                // GPU draws a different pattern from the same seed, so no image
                // could match the live first frame on every device.
                stableNoise: true,
                // Higher pressure packs more color zones into the same area, so each
                // moving shape is smaller (defaults are 3 / 5). Tune these two to
                // control blob size.
                horizontalPressure: 4,
                verticalPressure: 7,
                // Slower than the default (4) for a calmer drift.
                speed: 2.5,
                // Same vibrance as the FinalCTA gradient (library defaults) so the
                // top and bottom of the page read as the same gradient.
                colorSaturation: 8,
                highlights: 7,
                // Match the gradient's dark tone to the page gray (#1A1A1E) instead of
                // the default #18181C, so its dark areas blend invisibly into the
                // background rather than reading a touch darker.
                colors: [
                  { color: '#5B4C81', enabled: true },  // purple
                  { color: '#1A1A1E', enabled: true },  // dark = page gray
                  { color: '#1A1A1E', enabled: true },  // dark = page gray
                  { color: '#2C485E', enabled: true },  // blue
                  { color: '#2C485E', enabled: true },  // blue
                  { color: '#1A1A1E', enabled: true },  // dark = page gray (was mint, off)
                ],
              }}
            />
          )}
          <div
            id="hero"
            style={{
              position: 'relative',
              // Gray over the reading zone, fading to transparent so the single
              // shared gradient behind shows through as the hero's glow. Alpha drops
              // quickly after the text zone and reaches fully transparent by ~82%
              // (not 100%), so the gradient's vibrant colors read higher up — around
              // the top of the mockup. Top stays solid for text legibility.
              background: c.mode === 'dark'
                ? 'linear-gradient(to bottom, #1a1a1e 0%, #1a1a1e 18%, rgba(26,26,30,0.82) 28%, rgba(26,26,30,0.55) 38%, rgba(26,26,30,0.32) 48%, rgba(26,26,30,0.16) 58%, rgba(26,26,30,0.06) 70%, rgba(26,26,30,0) 82%)'
                : '#1a1a1e',
              paddingBottom: '3vh',
              zIndex: 1,
              overflow: 'hidden',
            }}
          >
          {/* Hero grain: its own layer and fade, between the gray color fade (the
              hero background) and the hero content. */}
          {c.mode === 'dark' && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                backgroundImage: `url("${HERO_GRAIN_DATA_URL}")`,
                maskImage: HERO_GRAIN_FADE,
                WebkitMaskImage: HERO_GRAIN_FADE,
              }}
            />
          )}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <HeroSection />
            {!isGenerating && <HeroMockup />}
          </div>
        </div>
        <div
          style={{ pointerEvents: isGenerating ? 'none' : 'auto', position: 'relative', zIndex: 2 }}
        >
          {/* Logo cycler — sits right under the hero mockup, above "What no one tells you". */}
          {/* TrustBand is an opaque gray plane rendered IN FRONT of the same
              gradient as the hero. The band CUTS the gradient with a well-defined
              bottom edge; directly below it the gradient is revealed crisp at the
              top (no fade-in) and fades out smoothly + quickly into the pain
              section. Dark mode only. Logos vertically centered (symmetric py). */}
          <Section bg={c.mode === 'dark' ? 'transparent' : '#E4D8CF'}>
            <Box>
            <Box
              id="trust-band-glass"
              sx={{
                py: { xs: 2, md: 3 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Frosted-glass band (dark mode) — EXACT match to the scrolled Nav
                // header glass: same tint, blur, bottom-only hairline border, and
                // grain overlay. The shared gradient behind shows through, softened.
                ...(c.mode === 'dark' && {
                  backgroundColor: 'rgba(16, 16, 22, 0.42)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  borderTop: '1.5px solid #1c1c22',
                  borderBottom: '1.5px solid #1c1c22',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url("${BAND_GRAIN_DATA_URL}")`,
                    backgroundRepeat: 'repeat',
                    pointerEvents: 'none',
                    opacity: 0.42,
                  },
                }),
              }}
            >
              <TrustBand knockout={c.mode === 'dark'} />
            </Box>
            {c.mode === 'dark' && <TrustLogoKnockout />}
            </Box>
          </Section>
          {/* Reveal window: transparent at the top so the SHARED hero gradient
              behind shows crisp right under the band, then fades to gray as it goes
              down into the pain section. No NeatBackground here — it's the hero's
              one gradient showing through. Dark mode only. */}
          {c.mode === 'dark' && (
            <Box
              aria-hidden="true"
              sx={{
                // Taller strip = the fade has more vertical distance to cover, so the
                // blend reads smoother. Height and marginTop grow together (below) so
                // the BOTTOM edge stays put — only the START of the fade moves up.
                height: 'clamp(340px, 45vh, 570px)',
                // Pull the whole fade strip UP so it starts higher (overlapping the
                // band — its transparent top keeps the band visible) and its bottom
                // ends further up, bringing the pain section closer to the band.
                // Kept in lockstep with height above so the bottom edge is unchanged.
                marginTop: 'clamp(-280px, -33vh, -220px)',
                // Eased fade — roughly linear through the first half, then the steps
                // get denser and closer in alpha toward the gray end (and the
                // high-alpha region starts further up) so the blend into solid
                // #1a1a1e is gradual instead of snapping over the last stretch. This
                // removes the faint seam where the strip used to meet the page gray.
                background: 'linear-gradient(to bottom, rgba(26,26,30,0) 0%, rgba(26,26,30,0.12) 12%, rgba(26,26,30,0.24) 24%, rgba(26,26,30,0.36) 36%, rgba(26,26,30,0.48) 47%, rgba(26,26,30,0.6) 57%, rgba(26,26,30,0.7) 66%, rgba(26,26,30,0.79) 74%, rgba(26,26,30,0.86) 81%, rgba(26,26,30,0.92) 88%, rgba(26,26,30,0.96) 94%, rgba(26,26,30,0.99) 98%, #1a1a1e 100%)',
                position: 'relative',
                // Grain continues below the band and fades out with the color into
                // the pain section. Masked off over the top of the strip — the part
                // pulled up behind the band (same value as -marginTop above) — so
                // it starts right at the band's bottom edge.
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url("${HERO_GRAIN_DATA_URL}")`,
                  pointerEvents: 'none',
                  maskImage: 'linear-gradient(to bottom, transparent clamp(220px, 33vh, 280px), black clamp(220px, 33vh, 280px), rgba(0,0,0,0.6) 70%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, transparent clamp(220px, 33vh, 280px), black clamp(220px, 33vh, 280px), rgba(0,0,0,0.6) 70%, transparent 100%)',
                },
              }}
            />
          )}
        </div>
        </Box>
        <div
          style={{ pointerEvents: isGenerating ? 'none' : 'auto', position: 'relative', zIndex: 2 }}
        >
          <Section
            id="pain"
            // Dark mode: the pain section rides up over the bottom of the reveal
            // strip (PAIN_OVERLAP) so its heading sits close under the band. Its
            // top PAIN_OVERLAP is see-through, letting the strip finish its fade
            // underneath — the strip ends on solid #1a1a1e, so there's no seam.
            bg={c.mode === 'dark' ? `linear-gradient(to bottom, transparent ${PAIN_OVERLAP}, #1a1a1e ${PAIN_OVERLAP})` : '#E4D8CF'}
            // Stacked above the archetypes section and not clipped, so the pain
            // section's data board can fade out over the top of the next section.
            style={c.mode === 'dark'
              ? { marginTop: `calc(-1 * ${PAIN_OVERLAP})`, overflow: 'visible', zIndex: 3 }
              : { overflow: 'visible', zIndex: 3 }}
          >
            <PainSection />
          </Section>
          <Suspense fallback={null}>
            <Section id="archetypes-intro" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><ChessArchetypeDarkSection scrollTo={scrollTo} /></Section>
          </Suspense>
          <Suspense fallback={null}>
            <Section id="how-it-works" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><StartYourAscension /></Section>
          </Suspense>
          <Suspense fallback={null}>
            {/* The header's "Features" link: the analysis and the training rooms. */}
            <Section id="features" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'} style={{ overflow: 'clip' }}>
              <AnalysisShowcase />
              <TrainingShowcase />
            </Section>
          </Suspense>
          <Suspense fallback={null}>
            <Section id="testimonials" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><PlayerTestimonials /></Section>
          </Suspense>
          <Suspense fallback={null}>
            <Section id="pricing" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><PremiumMembership /></Section>
          </Suspense>
          <Suspense fallback={null}>
            <Section bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><CoachComparison /></Section>
          </Suspense>
          <Suspense fallback={null}>
            <Section id="faq" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><FAQSection /><FinalCTA /></Section>
          </Suspense>
          <Suspense fallback={null}>
            <Section id="footer-section" bg={c.mode === 'dark' ? '#1a1a1e' : '#E4D8CF'}><Footer /></Section>
          </Suspense>
        </div>
    </Box>
  );
}
